import { memo } from "react";
import { classNames } from "helpers";
import { WAREHOUSE_WEEK_DAYS } from "services/api/fleet/warehouses/consts";
import { formatWarehouseHours, getWarehouseToday } from "services/api/fleet/warehouses/transformations";
import { WarehouseWorkingDay } from "services/api/fleet/warehouses/types";
import Tooltip from "ui/components/Tooltip/Tooltip";

export interface WorkHourCellProps {
  workingHours: WarehouseWorkingDay[] | null | undefined;
}

const dayShort = (day: number) => WAREHOUSE_WEEK_DAYS.find((weekDay) => weekDay.day === day)?.short ?? "";

/**
 * Sažetak tjedna: uzastopni dani s istim vremenom spajaju se u jedan redak, npr. "Pon do Pet" i
 * "08:00 do 16:00". Zatvoreni dani se izostavljaju.
 */
const summarizeWorkingHours = (days: WarehouseWorkingDay[]) => {
  const groups: { first: number; last: number; from: string | null; to: string | null }[] = [];

  days
    .filter((day) => day.isOpen)
    .forEach((day) => {
      const group = groups[groups.length - 1];
      if (group && group.last === day.day - 1 && group.from === day.from && group.to === day.to) {
        group.last = day.day;
      } else {
        groups.push({ first: day.day, last: day.day, from: day.from, to: day.to });
      }
    });

  return groups.map((group) => ({
    days:
      group.first === group.last
        ? dayShort(group.first)
        : group.last - group.first === 1
          ? `${dayShort(group.first)} i ${dayShort(group.last)}`
          : `${dayShort(group.first)} do ${dayShort(group.last)}`,
    hours: formatWarehouseHours(group.from, group.to),
  }));
};

/** Radno vrijeme u tablici: sažetak tjedna, a na hover cijeli tjedan s istaknutim današnjim danom. */
const WorkHourCell: React.FC<WorkHourCellProps> = ({ workingHours }) => {
  const days = workingHours ?? [];
  const groups = summarizeWorkingHours(days);

  if (groups.length === 0) {
    return <span className="text-sm text-slate-400">Nije upisano</span>;
  }

  const today = getWarehouseToday();

  return (
    <Tooltip
      decorate={
        <span className="inline-flex items-center gap-2 text-sm">
          <span className="text-slate-500">{groups[0].days}</span>
          <span className="text-slate-900">{groups[0].hours}</span>
          {groups.length > 1 && <span className="text-xs text-slate-400">+{groups.length - 1}</span>}
        </span>
      }
      position="bottom-left"
      size="medium"
    >
      <div className="min-w-[220px]">
        <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Radno vrijeme</div>
        {WAREHOUSE_WEEK_DAYS.map(({ day, label }) => {
          const workingDay = days.find((workingDay) => workingDay.day === day);
          const isToday = day === today;

          return (
            <div key={day} className="flex justify-between gap-4 text-sm mb-1">
              <span className={classNames(isToday ? "font-semibold text-white" : "text-slate-300")}>
                {label}
                {isToday && <span className="ml-2 text-xs text-amber-400">danas</span>}
              </span>
              <span className={classNames("font-medium", workingDay?.isOpen ? "" : "text-slate-400")}>
                {workingDay?.isOpen ? formatWarehouseHours(workingDay.from, workingDay.to) : "Zatvoreno"}
              </span>
            </div>
          );
        })}
      </div>
    </Tooltip>
  );
};

export default memo(WorkHourCell);
